import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar, { SidebarKey } from './Sidebar';
import ComingSoonPopup from './ComingSoonPopup';
import { IconSignout } from './Icons';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';

interface BreadcrumbSegment {
  label: string;
  to?: string;
}

interface AppShellProps {
  active: SidebarKey;
  breadcrumbs?: BreadcrumbSegment[];
  rightSlot?: ReactNode;
  children: ReactNode;
  credits?: number;
  creditsMax?: number;
  projectsUsed?: number;
  projectsMax?: number;
  defaultSidebarCollapsed?: boolean;
  contentClassName?: string;
  hideTopBar?: boolean;
}

export default function AppShell({
  active,
  breadcrumbs,
  rightSlot,
  children,
  credits,
  creditsMax,
  projectsUsed,
  projectsMax,
  defaultSidebarCollapsed = false,
  contentClassName,
  hideTopBar = false,
}: AppShellProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const [comingSoon, setComingSoon] = useState<null | 'community' | 'billing' | 'settings'>(null);

  const signOut = () => {
    logout();
    navigate('/', { replace: true });
    navigate('/login');
  };

  return (
    <div style={{ display: 'flex', height: '100dvh', background: 'var(--bg)', color: 'var(--text)' }}>
      <Sidebar
        active={active}
        credits={credits}
        creditsMax={creditsMax}
        projectsUsed={projectsUsed}
        projectsMax={projectsMax}
        defaultCollapsed={defaultSidebarCollapsed}
        onCommunityClick={() => setComingSoon('community')}
        onBillingClick={() => setComingSoon('billing')}
        onSettingsClick={() => setComingSoon('settings')}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!hideTopBar && (
          <div
            style={{
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '0 16px' : '0 36px',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
              {breadcrumbs?.map((seg, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
                  {i > 0 && <span className="muted">/</span>}
                  {seg.to ? (
                    <button
                      type="button"
                      onClick={() => navigate(seg.to!)}
                      className="eyebrow"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {seg.label}
                    </button>
                  ) : i === (breadcrumbs?.length ?? 0) - 1 ? (
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>{seg.label}</span>
                  ) : (
                    <span className="eyebrow">{seg.label}</span>
                  )}
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {rightSlot}
              <button className="btn ghost sm" type="button" onClick={signOut}>
                <IconSignout />
                {t('signOut', 'Sign out')}
              </button>
            </div>
          </div>
        )}

        <main className={contentClassName} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {children}
        </main>
      </div>

      {comingSoon && (
        <ComingSoonPopup
          isOpen
          onClose={() => setComingSoon(null)}
          title={
            comingSoon === 'community'
              ? t('comingSoon.community', 'Community')
              : comingSoon === 'billing'
              ? t('comingSoon.billing', 'Credits & billing')
              : t('comingSoon.settings', 'Settings')
          }
        />
      )}
    </div>
  );
}
